import type { QueryClient } from '@tanstack/react-query';
import {
	createRootRouteWithContext,
	type ErrorComponentProps,
	Link,
	Outlet,
	useRouter,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useEmbedBridge } from '@/hooks/use-embed-bridge';
import { themeRootOf, useEmbedContext } from '@/lib/embed-context';

interface RouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
	component: RootComponent,
	notFoundComponent: NotFound,
	errorComponent: ErrorComponent,
});

function RootComponent() {
	const { embedded, theme, onHostMessage } = useEmbedBridge();
	const embedContext = useEmbedContext();
	const router = useRouter();

	// Apply the requested theme to whichever element owns the `dark` class.
	//
	// In the SPA and the iframe that is <html>. In an in-page element it has to
	// be a wrapper *inside* the shadow root: Tailwind's dark variant compiles to
	// `:is(.dark *)`, which cannot match an ancestor across a shadow boundary, so
	// a class on <html> would silently do nothing — and toggling a class on the
	// school's <html> would be ours to do and not ours to own.
	const themeChoice = theme ?? embedContext.theme ?? null;
	useEffect(() => {
		if (!themeChoice) return;
		themeRootOf(embedContext)?.classList.toggle('dark', themeChoice === 'dark');
	}, [themeChoice, embedContext]);

	// Respond to host-driven navigation requests. `to` is an arbitrary string
	// from the host, so we push it through the router history directly rather
	// than the typed navigate() API.
	useEffect(() => {
		if (!embedded) return;
		return onHostMessage((msg) => {
			if (msg.type === 'reachout:navigate') {
				router.history.push(msg.to);
			}
		});
	}, [embedded, onHostMessage, router]);

	return <Outlet />;
}

function NotFound() {
	return (
		<div className="flex flex-1 flex-col items-center justify-center p-5 text-center">
			<h1 className="text-5xl font-semibold mb-3">404</h1>
			<h2 className="text-xl font-semibold mb-4">Page not found</h2>
			<p className="text-muted-foreground mb-8 max-w-md">
				The page you're looking for doesn't exist. Let's get you back to somewhere useful.
			</p>
			<Button asChild>
				<Link to="/">Go home</Link>
			</Button>
		</div>
	);
}

function ErrorComponent({ reset }: ErrorComponentProps) {
	return (
		<div className="flex flex-1 flex-col items-center justify-center p-5 text-center">
			<h2 className="text-2xl font-semibold mb-4">Something went wrong</h2>
			<p className="text-muted-foreground mb-8 max-w-md">
				An unexpected error occurred. You can try reloading this section or head back to the start.
			</p>
			<div className="flex gap-3">
				<Button onClick={() => reset()}>Try again</Button>
				<Button variant="outline" asChild>
					<a href="/">Go home</a>
				</Button>
			</div>
		</div>
	);
}
